import { useCallback } from "react";
import { Platform } from "react-native";
import * as WebBrowser from "expo-web-browser";
import { useNavigation } from "@react-navigation/native";

import analytics from "@/analytics";
import { useAuth } from "@/components/AuthContext";
import { createTest } from "@/components/http/events";
import { notify } from "@/components/shared/AppToast";
import { openSupport, useSupportStatus } from "@/components/support/config";
import { libraryAvailableFor } from "@/components/library/config";
import { t } from "@/localization";
import { trackedUrl } from "./logic";

const openExternal = (url) => {
  if (Platform.OS === "web") {
    window.open(url, "_blank", "noopener");
    return;
  }
  WebBrowser.openBrowserAsync(url).catch(() => {});
};

// What the buttons on a What's new card do. `onLeave` closes the dropdown
// before the app moves to another screen.
export function useWhatsNewActions(source, onLeave) {
  const navigation = useNavigation();
  const { broadcaster, prefer_multiple_tests, account_type } = useAuth();
  const support = useSupportStatus();

  const track = (entry, action) =>
    analytics.track("What's New Clicked", {
      item_id: entry.id,
      action,
      source,
    });

  // Only the actions this build can run; the rest hide their button.
  const canTry = useCallback(
    (entry) => {
      const tryIt = entry.try;
      if (!tryIt || tryIt.unknown) return false;
      if (tryIt.url) return true;
      if (tryIt.action === "openSupport") return support.available;
      // Download everything: only where this build has the screen.
      if (tryIt.action === "downloadEverything")
        return libraryAvailableFor(account_type);
      return true;
    },
    [support.available, account_type]
  );

  const openLink = (entry) => {
    track(entry, "learn_more");
    openExternal(trackedUrl(entry.link.url, { id: entry.id, source }));
  };

  const runTry = async (entry) => {
    const tryIt = entry.try;
    track(entry, "try");
    if (tryIt.url) {
      openExternal(trackedUrl(tryIt.url, { id: entry.id, source }));
      return;
    }
    onLeave?.();
    switch (tryIt.action) {
      case "createEvent":
        navigation.navigate("CreateEvent");
        break;
      case "createFundraiser":
        // "Schedule your live fundraiser" (CreateEvent's fundraiser mode).
        navigation.navigate("CreateEvent", { fundraiser: "1" });
        break;
      case "videoWall":
        navigation.navigate("VideoWall");
        break;
      case "plans":
        navigation.navigate("Plans", { source: "whats_new" });
        break;
      case "downloadEverything":
        navigation.navigate("DownloadEverything", { source: "whats_new" });
        break;
      case "team":
        // Profile › Team (Teams, business accounts).
        navigation.navigate("Team");
        break;
      case "openSupport":
        openSupport();
        break;
      case "startTest":
        try {
          // Same as "Start free test": reuse the account's test unless it
          // keeps several.
          const event = await createTest(broadcaster, !!prefer_multiple_tests);
          navigation.navigate("HowToStream", { event, slug: event.slug });
        } catch (err) {
          notify.error(t("Couldn't start a test"), t("Try again in a moment."));
        }
        break;
      default:
        break;
    }
  };

  return { canTry, openLink, runTry };
}
